/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { Injectable }            from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';

import { User       }            from '@/models/user';
import { IUserRow   }            from '@/screens/administration/i.user.row';
import { AuthenticationService } from "@/services/authentication.service";

@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class UserService
 *
 * @brief CRUD access to user models
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class UserService {

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService
  ) {


  }

  /** fetch the list of users */
  getAll() {

    return this.http.get<User[]>(`/backend/users/all`);
  }

  /** find a specific user (by OID) */
  getById(id: number) {

    return this.http.get(`/backend/users/list/${id}`);
  }

  /** fetch any headers we need to send along with our request */
  headers() : HttpHeaders {

    const user: User = this.auth.userOfSession;

    if (!user) {

      /* no user logged in */

      return null;
    }

    return new HttpHeaders().set('Authorization', user.token).set('Content-Type', 'application/json');
  }

  /** helper to just get a simple CSV style dump */
  dump(username: string='') {

    if (username.length != 0) {

      return this.http.get(`/backend/report/userdump/${username}`);
    }

    return this.http.get(`/backend/report/userdump`);
  }

  /** similar to dump() but must match userid */
  userByUserid(userid: string) {

    return this.http.get(`/backend/user/find/userid/${userid}`);
  }

  /** similar to dump() but must match email */
  userByEmail(email: string) {

    return this.http.get(`/backend/user/find/email/${email}`);
  }

  /** create a new user */
  register(user: User) {

    /* we currently deploy to public GKE cloud, so we can't allow rando user registration */

    return null;

    /*
    return this.http.post(`/backend/users/register`, user);
     */
  }

  hardDeleteUser(userid: string) {
    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.delete(`/backend/users/delete/hard/${userid}`, {
      headers
    });
  }

  /** delete a user, we don't actually delete, we just mark as deleted. */
  deleteUser(userid: string) {

    const upd: IUserRow = {
      is_deleted: true
    };

    return this.update(userid, upd);
  }

  /** undelete a user */
  undeleteUser(userid: string) {

    const upd: IUserRow = {
      is_deleted: false
    };

    return this.update(userid, upd);
  }

  /** promote a user to admin level */
  promoteUser(userid: string) {

    const upd: IUserRow = {
      is_superuser: true
    };

    return this.update(userid, upd);
  }

  /** remove admin level rights from a user */
  demote(userid: string) {

    const upd: IUserRow = {
      is_superuser: false
    };

    return this.update(userid, upd);
  }

  /** lock a user */
  lock(userid: string) {

    const upd: IUserRow = {
      is_suspended: true
    };

    return this.update(userid, upd);
  }

  /** unlock a user */
  unlock(userid: string) {

    const upd: IUserRow = {
      is_suspended: false
    };

    return this.update(userid, upd);
  }

  /** link this user to the given organizations (by OID or key) */
  linkAdminToOrg(userid: string, orgKeys: number[]|string[]) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.post(`/backend/org/admin/link/${userid}`, orgKeys, {
      headers
    });
  }

  /** unlink this user from the given organizations (by OID or key) */
  setAdminOrg(userid: string, orgKeys: number[]|string[]) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.post(`/backend/org/admin/set/${userid}`, orgKeys, {
      headers
    });
  }

  /** link the organization (oid) to the user (uid) */
  linkOrganization(oid: number, uid: number) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.put(`/backend/org/admin/link/${oid}/${uid}`, {
      headers
    });
  }

  /** unlink the organization (oid) from the user (uid) */
  unlinkOrganization(oid: number, uid: number) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.put(`/backend/org/admin/unlink/${oid}/${uid}`, {
      headers
    });
  }

  /** unlink this user from the given organizations (by OID or key) */
  unlinkAdminFromOrg(userid: string, orgKeys: number[]|string[]) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.post(`/backend/org/admin/unlink/${userid}`, orgKeys, {
      headers
    });
  }

  /** update a user */
  update(userid: string, data: IUserRow) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.post(`/backend/users/edit/${userid}`, data, {
      headers
    });
  }

  /** create a user */
  create(data: IUserRow) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.post(`/backend/users/create`, data, {
      headers
    });
  }

  /** fetch the set of IUserRow entries that are a partial match */
  autocomplete(term: string) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.get(`/backend/user/autocomplete/${term}`, {
      headers
    });
  }

  /** link the project (pid) to the user (uid) */
  linkMember(pid: number, uid: number) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.put(`/backend/project/member/link/${pid}/${uid}`, {
      headers
    });
  }

  /** unlink the project (pid) from the user (sid) */
  unlinkMember(pid: number, uid: number) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.put(`/backend/project/member/unlink/${pid}/${uid}`, {
      headers
    });
  }
}
